import styled from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { baseShadows } from "../../../design-system/tokens/shadows";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { Button as DsButton } from "../../../design-system/components/primitives/Button/Button";
import { Modal } from "../../../design-system/components/composite/Modal/Modal";

/** Header text block, shared with EmbedCodeModal and HubspotQueryEditorModal (they import it from here). */
export const HeaderIdentity = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  ${typographyMixin("body", 2)}
  color: ${rawColors.text.highEmphasis};
`;

/** GAP: F17 dialog radius is 20px; the borders scale jumps from 16px (2xl) to 24px (3xl). */
const SHARE_DIALOG_RADIUS = "20px";
/** GAP: F17 control rows are 52px (people rows) and 30px (role pills); no spacing steps. */
const SHARE_PERSON_ROW_HEIGHT = "52px";
const SHARE_ROLE_PILL_HEIGHT = "30px";
const SHARE_AVATAR_SIZE = spacing[8];
const SHARE_ROW_DIVIDER = rawColors.grey[100];

/** F17 panel 2: 600px dialog, 24px padding, 16px gap, no header/footer chrome. */
export const ShareDialog = styled(Modal)`
  &&& {
    border-radius: ${SHARE_DIALOG_RADIUS};
    box-shadow: ${baseShadows.light.xl};
    padding: ${spacing[6]};
    display: flex;
    flex-direction: column;
    gap: ${spacing[4]};
    overflow: visible;
    /* One height on every tab, so switching tabs never resizes the dialog; the tab body scrolls. */
    box-sizing: border-box;
    height: min(90vh, 54rem);
  }
`;

/** The active tab's content: fills the dialog under the tabs and scrolls inside it. */
export const TabBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

export const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

export const DialogTitle = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${typographyMixin("heading", "h4")}
  font-weight: 600;
  color: ${rawColors.text.highEmphasis};
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  padding: 0;
  border: none;
  border-radius: ${borderRadius.full};
  background: transparent;
  color: ${rawColors.text.highEmphasis};
  cursor: pointer;

  &:hover {
    background: ${rawColors.grey[100]};
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

/** Segmented Invite | Link | Embed switcher. */
export const TabBar = styled.div`
  display: flex;
  gap: ${spacing[0.5]};
  padding: 3px;
  border-radius: ${borderRadius.xl};
  background: ${rawColors.grey[100]};
  flex-shrink: 0;
`;

export const TabButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  height: ${spacing[8]};
  padding: 0;
  border: none;
  border-radius: ${borderRadius.lg};
  background: ${({ $active }) =>
    $active ? rawColors.structural.white : "transparent"};
  color: ${({ $active }) =>
    $active ? rawColors.text.highEmphasis : rawColors.text.mediumEmphasis};
  cursor: pointer;
  ${typographyMixin("caption", 2)}
  font-weight: 600;
  transition: background ${duration.fast} ${easing.smooth}, color ${duration.fast} ${easing.smooth};
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    color: ${rawColors.text.lowEmphasis};
  }

  &:hover:not([aria-selected="true"]):not(:disabled) {
    color: ${rawColors.text.highEmphasis};
  }
`;

export const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  min-height: 0;
`;

/** Input + role pill + Invite button on one row; the dropdown sits below it. */
export const MembersSearchBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
`;

export const InviteRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const InviteField = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  height: ${spacing[10]};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: 0 ${spacing[3]};
  box-sizing: border-box;
  border-radius: ${borderRadius.xl};
  border: 1.5px solid ${rawColors.stroke[200]};
  background: ${rawColors.structural.white};

  &:focus-within {
    border-color: ${rawColors.primary[500]};
  }
`;

export const InviteInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  ${typographyMixin("caption", 2)}
  color: ${rawColors.text.highEmphasis};

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }

  &:disabled {
    color: ${rawColors.text.lowEmphasis};
    cursor: not-allowed;
  }
`;

export const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${rawColors.text.mediumEmphasis};

  &:hover:not(:disabled) {
    color: ${rawColors.text.highEmphasis};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/** Pill that wraps a role select trigger. */
export const RolePill = styled.div<{ $large?: boolean; $bare?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  height: ${({ $large }) =>
    $large ? spacing[10] : SHARE_ROLE_PILL_HEIGHT};
  padding: 0 ${spacing[2.5]};
  box-sizing: border-box;
  border-radius: ${borderRadius.full};
  border: 1px solid
    ${({ $bare }) => ($bare ? "transparent" : rawColors.stroke[200])};
  ${typographyMixin("caption", 3)}
  font-weight: 600;
  color: ${rawColors.text.highEmphasis};
`;

export const PersonList = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 40vh;
  overflow-y: auto;
`;

export const PersonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-height: ${SHARE_PERSON_ROW_HEIGHT};
  box-sizing: border-box;
  border-bottom: 1px solid ${SHARE_ROW_DIVIDER};
  ${typographyMixin("caption", 2)}

  &:last-child {
    border-bottom: none;
  }
`;

export const Avatar = styled.span<{ $owner?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${SHARE_AVATAR_SIZE};
  height: ${SHARE_AVATAR_SIZE};
  border-radius: ${borderRadius.full};
  ${typographyMixin("caption", 4)}
  font-weight: 700;
  background: ${({ $owner }) =>
    $owner ? rawColors.brand.primaryDarker : rawColors.primary[200]};
  color: ${({ $owner }) =>
    $owner ? rawColors.structural.white : rawColors.primary[900]};
`;

export const PersonIdentity = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

export const PersonName = styled.strong`
  ${typographyMixin("caption", 2)}
  font-weight: 600;
  color: ${rawColors.text.highEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PersonMeta = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OwnerLabel = styled.span`
  flex: 0 0 auto;
  padding: 0 ${spacing[2.5]};
  ${typographyMixin("caption", 3)}
  font-weight: 600;
  color: ${rawColors.text.lowEmphasis};
`;

export const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${spacing[6]};
  height: ${spacing[6]};
  padding: 0;
  border: none;
  border-radius: ${borderRadius.full};
  background: none;
  cursor: pointer;
  color: ${rawColors.text.lowEmphasis};

  &:hover:not(:disabled) {
    color: ${rawColors.text.highEmphasis};
    background: ${rawColors.grey[100]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

export const ApiErrorBanner = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.accent.red500};
`;

/** Read-only box displaying a URL or embed code. */
/* Grows with its content and leaves scrolling to TabBody: a second scroller here capped the grid's height, so the sticky preview scrolled away. */
export const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${spacing[3]};
  border-radius: ${borderRadius.xl};
  background-color: ${rawColors.grey[50]};
  box-sizing: border-box;
`;

export const ContentText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  word-break: break-all;
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/** Pill primary action (Invite, Copy), 40px tall. */
export const PillButton = styled(DsButton)`
  &&& {
    height: ${spacing[10]};
    min-height: ${spacing[10]};
    padding: 0 ${spacing[5]};
    border-radius: ${borderRadius.full};
    white-space: nowrap;
    flex: 0 0 auto;
  }
`;

/** Text-style button that jumps to another tab. */
export const ChangeLink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  text-decoration: underline;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;
